/**
 * The React binding for the shared privacy session.
 *
 * Everything with a rule in it lives in `PrivacySessionController`; this file
 * only connects it to React and to the ports. That split is deliberate: the
 * fail-closed properties are the ones most worth testing without a renderer,
 * and a rule expressed as a `useEffect` is a rule whose timing depends on
 * whether a test wrapped something in `act`.
 *
 * One provider, placed once. A second one would be a second lease, a second
 * lock state and a second answer to "is the Journal unlocked" — and the surface
 * that asked the wrong one would prompt again, or worse, not prompt at all.
 */

import type { ReactNode } from "react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import type {
  JournalAuthenticationAvailability,
  JournalOpenLibrary,
  JournalVault,
} from "@ff/domain";
import type { JournalAuthReason } from "@ff/domain/ports";
import { JOURNAL_LOCK_ENABLED } from "@ff/store";
import { usePreferenceDefinition } from "../app-state/preferences";
import {
  useClock,
  useOptionalDeviceVerification,
  usePorts,
  useVisibility,
} from "../app-state/ports";
import { UNAVAILABLE_DEVICE_VERIFICATION } from "../platform/auth";
import type { JournalLockChangeResult } from "./lockChange";
import type { JournalPreviewVault } from "./previews";
import type { JournalLibraryLoader, PrivacySessionSnapshot } from "./sessionStore";
import { PrivacySessionController } from "./sessionStore";

export interface PrivacySession extends PrivacySessionSnapshot {
  readonly vault: JournalVault;
  readonly isUnlocked: boolean;
  readonly isAuthenticating: boolean;
  readonly hasVerifiedForegroundLease: boolean;
  readonly isPrivacySensitive: boolean;
  /** `null` unless the vault is revealed. There is no other way to reach it. */
  readonly library: JournalOpenLibrary | null;
  readonly previews: JournalPreviewVault;
  reveal(reason?: JournalAuthReason): Promise<boolean>;
  cancelReveal(): void;
  refreshLibrary(): Promise<boolean>;
  refreshAvailability(): Promise<JournalAuthenticationAvailability>;
  changeLock(requested: boolean): Promise<JournalLockChangeResult>;
  /** Registers this surface as privacy-sensitive for as long as it is mounted. */
  retainJournalNavigation(): () => void;
  retainComposer(): () => void;
}

const PrivacySessionContext = createContext<PrivacySessionController | null>(null);

export interface PrivacySessionProviderProps {
  /**
   * Reads everything a revealed Journal shows. Supplied by the composition root
   * rather than constructed here: the repository is P17's, and a privacy layer
   * that reached for it directly would be a privacy layer that could be made to
   * read while concealed.
   */
  readonly loadLibrary: JournalLibraryLoader;
  /** Releases the passphrase-derived key on foreground loss. */
  readonly dropKeyMaterial?: () => void;
  readonly previews?: JournalPreviewVault;
  readonly children?: ReactNode;
}

export function PrivacySessionProvider({
  loadLibrary,
  dropKeyMaterial,
  previews,
  children,
}: PrivacySessionProviderProps): ReactNode {
  const clock = useClock();
  const visibility = useVisibility();
  const preferences = usePorts().preferences;
  const verification = useOptionalDeviceVerification();
  const [storedLock, setStoredLock] = usePreferenceDefinition(JOURNAL_LOCK_ENABLED);

  /**
   * The controller is built once and outlives every re-render, so its closures
   * read the latest values through a ref rather than capturing the first
   * render's. A rebuilt controller would be a lost lease and a fresh prompt on
   * any unrelated re-render of the tree.
   */
  const latest = useRef({ loadLibrary, setStoredLock, dropKeyMaterial });
  latest.current = { loadLibrary, setStoredLock, dropKeyMaterial };

  const [controller] = useState(
    () =>
      new PrivacySessionController({
        // Absent means this build genuinely has no verification capability, and
        // the honest port for that reports `unavailable` — which every decision
        // downstream already handles, fail-closed.
        verification: verification ?? UNAVAILABLE_DEVICE_VERIFICATION,
        clock,
        loadLibrary: () => latest.current.loadLibrary(),
        readLockEnabled: () =>
          JOURNAL_LOCK_ENABLED.decode(preferences.read(JOURNAL_LOCK_ENABLED.key)),
        writeLockEnabled: (value) => {
          // Written through the preference observer so this tab's own settings
          // screen re-reads it, then read back: the durable value is what the
          // toggle must show, and a refused write leaves the previous one.
          latest.current.setStoredLock(value);
          return (
            JOURNAL_LOCK_ENABLED.decode(preferences.read(JOURNAL_LOCK_ENABLED.key)) ===
            value
          );
        },
        dropKeyMaterial: () => latest.current.dropKeyMaterial?.(),
        ...(previews === undefined ? {} : { previews }),
      }),
  );

  useEffect(() => {
    void controller.start();
  }, [controller]);

  useEffect(() => {
    // Primed from `current()` because nothing is replayed to a late subscriber,
    // and a tab restored straight into the background must not come up revealed.
    controller.handlePhase(visibility.current());
    return visibility.subscribe((phase) => {
      controller.handlePhase(phase);
    });
  }, [controller, visibility]);

  useEffect(() => {
    // Covers the setting changing anywhere: this tab's Settings screen, and
    // another tab of the same profile. `applyLockEnabled` is a no-op when the
    // controller already knows, so the change this session authorized does not
    // run twice.
    void controller.applyLockEnabled(storedLock);
  }, [controller, storedLock]);

  return (
    <PrivacySessionContext.Provider value={controller}>
      {children}
    </PrivacySessionContext.Provider>
  );
}

function usePrivacySessionController(): PrivacySessionController {
  const controller = useContext(PrivacySessionContext);
  if (controller === null) {
    throw new Error(
      "usePrivacySession() requires an enclosing <PrivacySessionProvider>. One " +
        "session is shared by every Journal surface; a surface that built its " +
        "own would have its own lease and would prompt again.",
    );
  }
  return controller;
}

export function usePrivacySession(): PrivacySession {
  const controller = usePrivacySessionController();
  const snapshot = useSyncExternalStore(
    useCallback((listener) => controller.subscribe(listener), [controller]),
    useCallback(() => controller.snapshot(), [controller]),
    useCallback(() => controller.snapshot(), [controller]),
  );

  return useMemo<PrivacySession>(
    () => ({
      ...snapshot,
      isUnlocked: snapshot.vault.state === "revealed",
      isAuthenticating: snapshot.vault.state === "authenticating",
      hasVerifiedForegroundLease: controller.hasVerifiedForegroundLease(),
      isPrivacySensitive: controller.isPrivacySensitive(),
      library: controller.library(),
      previews: controller.previews(),
      reveal: (reason) => controller.reveal(reason),
      cancelReveal: () => {
        controller.cancelReveal();
      },
      refreshLibrary: () => controller.refreshLibrary(),
      refreshAvailability: () => controller.refreshAvailability(),
      changeLock: (requested) => controller.changeLock(requested),
      retainJournalNavigation: () => controller.retainJournalNavigation(),
      retainComposer: () => controller.retainComposer(),
    }),
    [controller, snapshot],
  );
}

/**
 * Entry content, or `null`.
 *
 * Separate from `usePrivacySession()` so a surface that only renders prose does
 * not re-render on every availability probe, and so the one accessor is easy to
 * grep for in review.
 */
export function useJournalLibrary(): JournalOpenLibrary | null {
  return usePrivacySession().library;
}

export function useJournalPreviews(): JournalPreviewVault {
  return usePrivacySessionController().previews();
}

/**
 * Marks the Journal destination as on screen for as long as this component is
 * mounted, which is what raises the window-level shield (F-JP-14).
 *
 * Sensitivity is a property of what is on screen, not of the lock: an open
 * composer is sensitive with the lock off, because the OS snapshots the app
 * switcher regardless of what the app thinks is protected.
 */
export function useJournalNavigationActive(active = true): void {
  const controller = usePrivacySessionController();
  useEffect(() => {
    if (!active) return;
    return controller.retainJournalNavigation();
  }, [controller, active]);
}

export function useComposerPresented(presented = true): void {
  const controller = usePrivacySessionController();
  useEffect(() => {
    if (!presented) return;
    return controller.retainComposer();
  }, [controller, presented]);
}
